/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Modal } from '../modals/Modal';
import {
  Pressable,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../theme/public-api';
import { ticketChrome } from './ticketChrome.styles';

type Props = {
  visible: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Desktop-style centered dialog vs bottom sheet. */
  variant?: 'sheet' | 'dialog';
  dialogStyle?: StyleProp<ViewStyle>;
  /** Size sheet to content instead of filling the screen. */
  fitContent?: boolean;
};

export function TicketSheet({
  visible,
  title,
  subtitle,
  onClose,
  children,
  footer,
  variant = 'sheet',
  dialogStyle,
  fitContent = false,
}: Props) {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  // En desktop, el variant "sheet" se comporta como "dialog" automáticamente.
  const effectiveVariant = variant === 'dialog' || width >= 768 ? 'dialog' : 'sheet';

  if (effectiveVariant === 'dialog') {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={[ticketChrome.sheetBackdrop, { justifyContent: 'center', alignItems: 'center' }]}>
          <Pressable style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} onPress={onClose} />
          <View style={[ticketChrome.dialogCard, dialogStyle]}>
            <View style={[ticketChrome.sheetHeader, { paddingTop: 16 }]}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={ticketChrome.sheetTitle}>{title}</Text>
                {subtitle ? <Text style={ticketChrome.sheetSubtitle}>{subtitle}</Text> : null}
              </View>
              <TouchableOpacity
                onPress={onClose}
                style={ticketChrome.sheetCloseBtn}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
              </TouchableOpacity>
            </View>
            {children}
            {footer}
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={ticketChrome.sheetBackdrop}>
        <Pressable style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} onPress={onClose} />
        <View
          style={[
            ticketChrome.sheetCard,
            { paddingBottom: Math.max(insets.bottom, spacing.sm) },
            !fitContent && { flex: 1 },
          ]}
        >
          <View style={ticketChrome.grabber} />
          <View style={ticketChrome.sheetHeader}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={ticketChrome.sheetTitle}>{title}</Text>
              {subtitle ? <Text style={ticketChrome.sheetSubtitle}>{subtitle}</Text> : null}
            </View>
            <TouchableOpacity
              onPress={onClose}
              style={ticketChrome.sheetCloseBtn}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
            </TouchableOpacity>
          </View>
          {children}
          {footer}
        </View>
      </View>
    </Modal>
  );
}
